/**
 * Lykke's composer: the universal agent composer (shell/AgentComposer.tsx) wired to her. Write, dictate, call her,
 * attach files (drag and drop, paste, the paperclip). Just an input: no suggestion chips (house rule). No
 * page-layout assumptions: the home bar, the Lykke page, the dock and the new layout's panel mount it as they need.
 *
 *   <LykkeComposer scope onSend size placeholder autoFocus busy />
 *   onSend(text, files, raw) gets the person's words, the files she reads (pictures, PDFs, text: up to 3, 5 MB each,
 *   as base64) and the same files as File objects. A file she cannot read in a turn (a video, a Word file) gets one
 *   line saying where it can go instead; it is not sent.
 */
import { forwardRef } from 'react';
import { AgentComposer, DEFAULT_ACCEPT, type AgentComposerHandle, type CallPhase } from '@/cowork/shell/AgentComposer';
import { useLykke, type Scope, type VoiceState } from './LykkeContext';
import type { LykkeFileIn } from './lykkeApi';

const MAX_FILES = 3;
const MAX_BYTES = 5 * 1024 * 1024;
const CALL_PHASE: Record<VoiceState, CallPhase> = { off: 'off', connecting: 'connecting', listening: 'listening', thinking: 'thinking', speaking: 'speaking' };

/** What Lykke reads in a turn (cowork engine/lykke/turn.ts cleanFiles): pictures, PDFs and text files. */
// eslint-disable-next-line react-refresh/only-export-components -- shared with the tests
export function lykkeReads(f: { name: string; type: string; size?: number }): boolean {
  const mime = (f.type || '').toLowerCase();
  const ok = /^image\/(png|jpe?g|gif|webp)$/.test(mime) || mime === 'application/pdf' || /^text\//.test(mime) || /\.(md|csv|txt)$/i.test(f.name);
  return ok && (f.size === undefined || f.size <= MAX_BYTES);
}

function readFile(f: File): Promise<LykkeFileIn> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => { const s = String(r.result || ''); resolve({ name: f.name, mime: f.type || (f.name.endsWith('.md') ? 'text/markdown' : f.name.endsWith('.csv') ? 'text/csv' : 'text/plain'), data: s.slice(s.indexOf(',') + 1) }); };
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(f);
  });
}

export interface LykkeComposerProps {
  /** The conversation the call talks to. */
  scope: Scope;
  onSend: (text: string, files: LykkeFileIn[], raw: File[]) => void;
  size?: 'large' | 'compact';
  placeholder: string;
  autoFocus?: boolean;
  busy?: boolean;
  className?: string;
}

export const LykkeComposer = forwardRef<AgentComposerHandle, LykkeComposerProps>(function LykkeComposer({ scope, onSend, size = 'compact', placeholder, autoFocus, busy, className }, ref) {
  const l = useLykke();
  if (!l?.available) return null;
  const callHere = l.voiceScope === scope && l.voice !== 'off';
  return (
    <AgentComposer
      ref={ref}
      agentName="Lykke"
      size={size === 'large' ? 'hero' : 'panel'}
      className={className}
      placeholder={placeholder}
      autoFocus={autoFocus}
      busy={busy}
      filesAlone
      accept={DEFAULT_ACCEPT}
      maxFiles={MAX_FILES}
      onSend={async (text, files) => {
        if (busy) return false;
        const readable = files.filter((f) => lykkeReads(f.file));
        const other = files.filter((f) => !lykkeReads(f.file));
        if (other.length) {
          const big = other.filter((f) => lykkeReads({ name: f.name, type: f.file.type }));
          const video = other.some((f) => f.kind === 'video');
          l.note(scope, big.length
            ? `${big.map((f) => f.name).join(', ')}: over 5 MB, so not sent. Send a smaller file.`
            : `${other.map((f) => f.name).join(', ')}: I read pictures, PDFs and text in a message.${video ? ' A video goes on a slide in the Deck studio.' : ' Save a Word or slide file as PDF and send it again.'}`);
        }
        if (!text && !readable.length) return;
        const read = (await Promise.all(readable.map((f) => readFile(f.file).catch(() => null)))).filter((x): x is LykkeFileIn => !!x);
        onSend(text, read, readable.map((f) => f.file));
      }}
      call={l.voiceSupported ? {
        phase: callHere ? CALL_PHASE[l.voice] : 'off',
        caption: callHere ? l.caption || undefined : undefined,
        muted: l.muted,
        start: () => l.startVoice(scope),
        end: () => l.stopVoice(),
        mute: (m) => l.setMuted(m),
      } : null}
    />
  );
});
